import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';

interface AdminRouteProps {
  children: React.ReactNode;
}

const LoadingContainer = styled(Box)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { student, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress size={60} sx={{ color: 'white', mb: 2 }} />
        <Typography variant="h6" fontWeight={600}>
          Loading...
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Verifying your session
        </Typography>
      </LoadingContainer>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has admin role
  if (student?.role !== 'admin') {
    return (
      <LoadingContainer>
        <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}>
          Access Denied: Admin privileges required
        </Alert>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          You don't have permission to access this page
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center' }}>
          This page is restricted to administrators only.<br/>
          Please contact your administrator if you believe this is an error.
        </Typography>
      </LoadingContainer>
    );
  }

  // Render admin content
  return <>{children}</>;
};

export default AdminRoute; 